import { FC } from 'react';
import { Divider, Flex, VStack } from '@chakra-ui/react';
import { Icon } from '@shinetools/sunshine-universal';

import { formatAmount } from 'common/formatAmount';
import formatPercent from 'common/formatPercent';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import * as ContentLayout from 'layouts/ContentLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import BankAccountsDetails from '../../components/BankAccountsDetails';
import useBankTransferData from '../../hooks/useBankTransferData';
import { useInternationalTransferPayoutFees } from './hooks/useInternationalTransferPayoutFees';
import locales from './locales';
import { getFormattedExchangeRate } from './utils/getFormattedExchangeRate';

const InternationalTransferRecap: FC<StepProps<BankTransferCreationStep>> = (
  props,
) => {
  const { bankTransfer } = useBankTransferContext('internationalTransferRecap');
  const { additionalInformation, fromBankAccountId, quote, recipient } =
    bankTransfer;

  const { data, error, loading, refetch } = useBankTransferData({
    fromBankAccountId,
  });

  const {
    error: feesError,
    internationalTransferPayoutFees,
    isLoading: isLoadingFees,
    refetch: refetchFees,
  } = useInternationalTransferPayoutFees();

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetch} />
      </ErrorLayout>
    );
  }

  if (feesError) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchFees} />
      </ErrorLayout>
    );
  }

  if (loading || isLoadingFees) {
    return <Loader />;
  }

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="InternationalTransferRecap" />

        <Layout.HeaderButtonsContainer>
          <BackButton onClick={props.goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <ContentLayout.Header title={locales.checkDetails} />

          <VStack
            alignItems="stretch"
            paddingBottom="space-32"
            spacing="space-48"
            width="100%"
          >
            <BankAccountsDetails
              emittingBankAccount={data.fromBankAccount}
              internationalBankTransferRecipient={recipient}
            />

            <VStack alignItems="stretch" spacing="space-8">
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {locales.transferRecap}
              </Text>
              <SunshineCard.Group>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.targetAmount}</Text>
                    <Text fontWeight="weight-500" variant="primary">
                      {formatAmount({
                        currency: quote.target.currency,
                        currencyDisplay: 'code',
                        forcePlusSign: false,
                        value: quote.target.amountFloat,
                      })}
                    </Text>
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.exchangeRate}</Text>

                    <Text variant="primary">
                      {getFormattedExchangeRate({
                        rate: quote.rate,
                        sourceCurrency: quote.source.currency,
                        targetCurrency: quote.target.currency,
                      })}
                    </Text>
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.convertedAmount}</Text>
                    <Text variant="primary">
                      {formatAmount({
                        currency: quote.source.currency,
                        currencyDisplay: 'symbol',
                        forcePlusSign: false,
                        value: quote.target.amountFloat / quote.rate,
                      })}
                    </Text>
                  </SunshineCard.Content>
                </SunshineCard>

                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.commission}</Text>
                    <Flex flexDirection="row" gap="space-4">
                      <Tooltip
                        description={locales.formatString(
                          locales.commissionDetails,
                          {
                            minimumFee: formatAmount({
                              currency: quote.fee.currency,
                              currencyDisplay: 'symbol',
                              forcePlusSign: false,
                              isMinorUnitValue: true,
                              value: internationalTransferPayoutFees.minimum,
                            }),
                            variableFee: formatPercent(
                              internationalTransferPayoutFees.variable,
                            ),
                          },
                        )}
                      >
                        <SunshineIcon
                          color="grey.600"
                          marginTop="2px"
                          name="information"
                        />
                      </Tooltip>
                      <Text variant="primary">
                        {locales.formatString(locales.amountExcludingVAT, {
                          amount: formatAmount({
                            currency: quote.fee.currency,
                            currencyDisplay: 'symbol',
                            forcePlusSign: false,
                            value: quote.fee.amountFloat,
                          }),
                        })}
                      </Text>
                    </Flex>
                  </SunshineCard.Content>
                </SunshineCard>
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                  >
                    <Text>{locales.totalAmount}</Text>
                    <Flex flexDirection="row" gap="space-4">
                      <Text fontWeight="weight-500" variant="primary">
                        {formatAmount({
                          currency: quote.source.currency,
                          currencyDisplay: 'symbol',
                          forcePlusSign: false,
                          value: quote.source.amountFloat,
                        })}
                      </Text>
                    </Flex>
                  </SunshineCard.Content>
                </SunshineCard>
              </SunshineCard.Group>
            </VStack>
            <VStack alignItems="stretch" spacing="space-8">
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {locales.additionalInformation}
              </Text>
              <SunshineCard.Group>
                {typeof additionalInformation.reference === 'string' ? (
                  <SunshineCard>
                    <SunshineCard.Content
                      flexDirection="row"
                      justifyContent="space-between"
                      spacing="space-24"
                      textAlign="right"
                    >
                      <Text>{locales.label}</Text>

                      <Text variant="primary">
                        {additionalInformation.reference}
                      </Text>
                    </SunshineCard.Content>
                  </SunshineCard>
                ) : null}
                <SunshineCard>
                  <SunshineCard.Content
                    flexDirection="row"
                    justifyContent="space-between"
                    spacing="space-24"
                    textAlign="right"
                  >
                    <Text>{locales.transferType}</Text>
                    <Flex flexDirection="row" gap="space-4">
                      <Icon
                        color="$grey.600"
                        icon="earth"
                        marginTop={2}
                        size="small"
                      />
                      <Text variant="primary">
                        {locales.internationalTransfer}
                      </Text>
                    </Flex>
                  </SunshineCard.Content>
                </SunshineCard>
              </SunshineCard.Group>
            </VStack>
          </VStack>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <Divider />
          <Layout.FooterButtonsContainer>
            <Button
              icon="arrow-right"
              iconPosition="right"
              onClick={() => props.goNext()}
            >
              {locales.confirm}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default InternationalTransferRecap;
